export type AnalyticsEventName =
  | 'player_session'
  | 'game_start'
  | 'round_complete'
  | 'game_complete'
  | 'multiplayer_create'
  | 'multiplayer_join'
  | 'invite_share'
  | 'game_result_share'
  | 'registration_start'
  | 'registration_complete'
  | 'daily_hand_start'
  | 'daily_hand_answer'
  | 'daily_hand_complete'
  | 'daily_hand_share';

export type AnalyticsValue = string | number | boolean;
export type AnalyticsParams = Record<string, AnalyticsValue | null | undefined>;
export type AnalyticsConsentState = 'unknown' | 'denied' | 'granted';

const FORBIDDEN_PARAM_NAMES = new Set([
  'email',
  'name',
  'display_name',
  'displayname',
  'player_name',
  'opponent_name',
  'uid',
  'user_id',
  'userid',
  'match_code',
  'matchcode',
  'phone',
]);

const FORBIDDEN_PARAM_PARTS = new Set([
  'email',
  'phone',
  'uid',
  'userid',
  'matchcode',
]);

const MAX_STRING_LENGTH = 100;
const RETURNING_PLAYER_KEY = 'sbarazzino:analytics:seen';
const onceKeys = new Set<string>();

let analyticsConsent: AnalyticsConsentState = 'unknown';
let analyticsInitialized = false;
let originalGtag: ((...args: unknown[]) => void) | undefined;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function isForbiddenParamName(key: string): boolean {
  if (FORBIDDEN_PARAM_NAMES.has(key)) return true;
  const compact = key.replace(/[^a-z0-9]/g, '');
  return FORBIDDEN_PARAM_PARTS.has(compact);
}

export function buildAnalyticsPayload(params: AnalyticsParams = {}): Record<string, AnalyticsValue> {
  return Object.entries(params).reduce<Record<string, AnalyticsValue>>((result, [key, value]) => {
    const normalizedKey = key.trim().toLowerCase();
    if (!normalizedKey || isForbiddenParamName(normalizedKey)) return result;
    if (value === null || value === undefined) return result;

    if (typeof value === 'string') {
      const normalizedValue = value.trim().slice(0, MAX_STRING_LENGTH);
      if (normalizedValue) result[normalizedKey] = normalizedValue;
      return result;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      result[normalizedKey] = value;
      return result;
    }

    if (typeof value === 'boolean') result[normalizedKey] = value;
    return result;
  }, {});
}

function asCommand(entry: unknown): unknown[] | null {
  if (Array.isArray(entry)) return entry;
  if (!entry || typeof entry !== 'object' || !('length' in entry)) return null;

  const length = Number((entry as { length?: unknown }).length);
  if (!Number.isInteger(length) || length < 0 || length > 20) return null;

  try {
    return Array.from(entry as ArrayLike<unknown>);
  } catch {
    return null;
  }
}

function readConsentCommand(args: unknown[]): void {
  if (args[0] !== 'consent') return;
  if (args[1] !== 'default' && args[1] !== 'update') return;

  const values = args[2];
  if (!values || typeof values !== 'object') return;

  const analyticsStorage = (values as Record<string, unknown>).analytics_storage;
  if (analyticsStorage === 'granted') {
    setAnalyticsConsent('granted');
  } else if (analyticsStorage === 'denied') {
    setAnalyticsConsent('denied');
  }
}

function getReturningPlayerState(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const returningPlayer = window.localStorage.getItem(RETURNING_PLAYER_KEY) === '1';
    window.localStorage.setItem(RETURNING_PLAYER_KEY, '1');
    return returningPlayer;
  } catch {
    return false;
  }
}

function getEntryAttribution(): AnalyticsParams {
  if (typeof window === 'undefined') return {};

  const search = new URLSearchParams(window.location.search);
  const read = (key: string): string | undefined => {
    const value = search.get(key)?.trim().toLowerCase();
    return value && /^[a-z0-9._-]{1,50}$/.test(value) ? value : undefined;
  };

  return {
    entry_source: read('utm_source'),
    entry_medium: read('utm_medium'),
    entry_campaign: read('utm_campaign'),
  };
}

function trackSessionStart(): void {
  if (typeof window === 'undefined') return;

  trackEventOnce('player_session', 'current-page-session', {
    returning_player: getReturningPlayerState(),
    entry_surface: window.location.pathname.startsWith('/play') ? 'play' : 'editorial',
    ...getEntryAttribution(),
  });
}

export function setAnalyticsConsent(state: AnalyticsConsentState): void {
  const wasGranted = analyticsConsent === 'granted';
  analyticsConsent = state;

  if (!wasGranted && state === 'granted') {
    trackSessionStart();
  }
}

export function getAnalyticsConsent(): AnalyticsConsentState {
  return analyticsConsent;
}

/**
 * Observes Consent Mode commands without replacing Google's dataLayer contract.
 * Product events remain blocked until analytics_storage is explicitly granted.
 */
export function initializeAnalytics(): void {
  if (typeof window === 'undefined' || analyticsInitialized) return;

  originalGtag = window.gtag;
  window.gtag = (...args: unknown[]) => {
    if (originalGtag) {
      originalGtag(...args);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(args);
    }

    // Forward the consent update to Google's queue BEFORE emitting
    // player_session. Otherwise the first session event carries G100 (denied)
    // even when the user has just opted into analytics.
    readConsentCommand(args);
  };

  // Mark initialized before replaying queued commands. A queued grant can emit
  // player_session, which must not recursively initialize the observer.
  analyticsInitialized = true;

  for (const entry of window.dataLayer || []) {
    const command = asCommand(entry);
    if (command) readConsentCommand(command);
  }

  if (analyticsConsent === 'granted') {
    trackSessionStart();
  }
}

export function trackEvent(name: AnalyticsEventName, params: AnalyticsParams = {}): boolean {
  if (typeof window === 'undefined') return false;
  if (!analyticsInitialized) initializeAnalytics();

  const payload = buildAnalyticsPayload(params);

  if (analyticsConsent !== 'granted') {
    if (import.meta.env.DEV) {
      console.debug('[analytics:blocked]', name, payload, { consent: analyticsConsent });
    }
    return false;
  }

  if (import.meta.env.DEV) {
    console.debug('[analytics]', name, payload);
    return true;
  }

  if (typeof window.gtag === 'function') {
    window.gtag('event', name, payload);
    return true;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(['event', name, payload]);
  return true;
}

export function trackEventOnce(
  name: AnalyticsEventName,
  dedupeKey: string,
  params: AnalyticsParams = {},
): boolean {
  const key = `${name}:${dedupeKey}`;
  if (onceKeys.has(key)) return false;

  const emitted = trackEvent(name, params);
  if (emitted) onceKeys.add(key);
  return emitted;
}

export function createCampaignUrl(
  path: string,
  campaign: string,
  medium: 'social' | 'creator' | 'share' | 'product' = 'social',
  source = 'instagram',
): string {
  const base = typeof window === 'undefined' ? 'https://sbarazzino.it' : window.location.origin;
  const url = new URL(path, base);
  url.searchParams.set('utm_source', source);
  url.searchParams.set('utm_medium', medium);
  url.searchParams.set('utm_campaign', campaign);
  return url.toString();
}

/** Test-only state reset. It is intentionally explicit so tests never leak consent. */
export function resetAnalyticsStateForTests(): void {
  analyticsConsent = 'unknown';
  analyticsInitialized = false;
  originalGtag = undefined;
  onceKeys.clear();
}
